import { parse, stringify } from 'query-string';

import { AVAILABLE_DEEP_SEARCH_TAGS } from '../constants/label';
import { ACCESS_PUBLIC } from '../constants/video';
import { ALL_SOCIAL_ENGAGEMENT_TYPES, LIKES, SHARES, VIEWS } from '../constants/videoEngagement';

import { deepClone } from '@/modules/common/helpers/object';
import { setSourceCache, updateSourceCache } from '@/modules/common/helpers/videoCache';

import { collectVideoEntities } from './chips';
import { replaceDomain } from './url';

let wid = null;

export const setPlayerName = (playerName) => {
  wid = playerName;
};

/**
 * @param {Object} video formatted video
 * @param {number} signedUrlExp resource url expiration time
 */
const grepVideoResources = (video, signedUrlExp) => {
  const iterateVideoKey = (param) => {
    if (Array.isArray(param)) {
      param.forEach((item) => iterateVideoKey(item));
    } else if (typeof param === 'object' && param !== null) {
      Object.keys(param).forEach((key) => iterateVideoKey(param[key]));
    } else if (typeof param === 'string') {
      try {
        const url = new URL(param);
        const parsedSearchParams = parse(url.search);
        const numericValues = Object.values(parsedSearchParams).map((value) => parseInt(value, 10));

        if (numericValues.includes(signedUrlExp)) {
          setSourceCache(video.mediaId, param, signedUrlExp);
        }
      } catch {
        /* empty */
      }
    }
  };
  iterateVideoKey(video);
};

/**
 * @typedef {string} OldUrl
 *
 * @typedef {Object} Resource
 * @property {string} newUrl
 * @property {Date} signedUrlExp
 *
 * @param {Object} video formatted video
 * @param {Object.<OldUrl, Resource>} resourceMap resources to update
 */
export const updateVideoResources = (video, resourceMap) => {
  function updateValue(value) {
    if (typeof value === 'string' && resourceMap[value]) {
      updateSourceCache(video.mediaId, value, resourceMap[value].newUrl, resourceMap[value].signedUrlExp);
      return resourceMap[value].newUrl;
    }
    if (Array.isArray(value)) {
      return value.map((item) => updateValue(item));
    }
    if (typeof value === 'object' && value !== null) {
      Object.keys(value).forEach((key) => {
        // eslint-disable-next-line no-param-reassign
        value[key] = updateValue(value[key]);
      });
    }
    return value;
  }

  Object.keys(video).forEach((key) => {
    // eslint-disable-next-line no-param-reassign
    video[key] = updateValue(video[key]);
  });
};

const setWidQuery = (sourceUrl, signedUrlExp) => {
  const [url, stringQuery] = sourceUrl.split('?');

  const parsedParams = parse(stringQuery);

  if (!signedUrlExp) {
    parsedParams.wid = wid;
  }

  return `${url}?${stringify(parsedParams)}`;
};

const processUrl = (urlSource, signedUrlExp) => setWidQuery(replaceDomain(urlSource), signedUrlExp);

export const getFormattedVideo = (video, options) => {
  const $$sourceVideo = deepClone(video);

  const formattedVideo = {
    ...video,
    mediaId: video.mediaId ?? video.mediaid,
    images: video.images.map((video$) => ({
      ...video$,
      file: processUrl(video$.file, options.signedUrlExp),
    })),
    files: (video.files ?? []).map((file$) => ({
      ...file$,
      file: processUrl(file$.file, options.signedUrlExp),
    })),
    file: video.file ?? '',
    ccFiles: (video.ccFiles ?? []).map((file$) => ({
      ...file$,
      file: processUrl(file$.file, options.signedUrlExp),
    })),
    previewUrl: video.previewUrl ? processUrl(video.previewUrl, options.signedUrlExp) : '',
    plot: video.plot ?? '',
    duration: video.duration ?? 0,
    created: video.created ?? 0,
    performanceData: {
      time: video.performanceData.time ?? 0,
      parameters: ALL_SOCIAL_ENGAGEMENT_TYPES.map((type) => {
        const serverItem = video.performanceData.parameters.find((param$) => param$.type === type);

        return {
          type,
          value: serverItem?.value ?? 0,
        };
      }),
    },
    trending: video.trending ?? 0,
    ccUrl: video.ccUrl ? processUrl(video.ccUrl, options.signedUrlExp) : '',
    describe: (video.describe || []).filter((item) => AVAILABLE_DEEP_SEARCH_TAGS.includes(item.type)),
    highlight: (video.highlight ?? []).map((oneHighlight) => ({
      ...oneHighlight,
      stopWords: (oneHighlight.stopWords ?? []).slice().sort((a, b) => b.length - a.length),
      fragments: oneHighlight.fragments.slice().sort((a, b) => b.length - a.length),
    })),
    videoId: video.videoId ?? null,
    highlights: video.highlights ?? false,
    slides: video.slides ?? false,
    access: {
      users: video.access?.users ?? [],
      level: video.access?.level ?? ACCESS_PUBLIC,
    },
    videoEntities: collectVideoEntities(video),
    $$sourceVideo,
  };

  if (video.hlsFile?.file) {
    formattedVideo.hlsFile = {
      ...video.hlsFile,
      file: processUrl(video.hlsFile.file, options.signedUrlExp),
    };
  }

  if (options.signedUrlExp) {
    grepVideoResources(formattedVideo, options.signedUrlExp);
  }

  return formattedVideo;
};

const siSymbolList = ['', 'K', 'M', 'B', 'T', 'Qa', 'Qi'];
export const abbreviateNumber = (number) => {
  const int = parseInt(number, 10);
  const tier = Math.floor(Math.log10(int || 1) / 3);

  const scaled = (int / 1000 ** tier).toFixed(1).replace(/\.0+$/, '');

  return `${scaled}${siSymbolList[tier]}`;
};

export const getOriginalVideoEngagementData = (performanceDataParameters) =>
  ALL_SOCIAL_ENGAGEMENT_TYPES.reduce(
    (acc, type) => ({
      ...acc,
      [type]: performanceDataParameters.find((item$) => item$.type === type)?.value ?? 0,
    }),
    {},
  );

const socialTypeMinValue = {
  [VIEWS]: 10,
  [LIKES]: 1,
  [SHARES]: 1,
};
export const getVideoEngagementData = (performanceDataParameters, engagement, showLike = false) =>
  ALL_SOCIAL_ENGAGEMENT_TYPES.reduce((acc, type) => {
    const item = engagement.includes(type) && performanceDataParameters.find((item$) => item$.type === type);

    const value = item?.value ?? 0;

    if ((showLike && type === LIKES) || value >= socialTypeMinValue[type]) {
      return {
        ...acc,
        [type]: value,
      };
    }

    return acc;
  }, {});

export const filterVideosByChip = (videos, allowedChipTypes, selectedChip) => {
  if (!selectedChip) {
    return videos;
  }

  const selectedChipLowerCase = selectedChip.toLowerCase();

  return videos.filter((video) =>
    video.videoEntities.some(
      (videoEntity) =>
        allowedChipTypes.includes(videoEntity.type) && videoEntity.value.toLowerCase() === selectedChipLowerCase,
    ),
  );
};
